import * as R from 'ramda'
import { mapObjectKeys } from 'sidecar-utility-library'
import { parseFromCamelCase, humanizeString } from 'sidecar-utilities'

import { parsePharmacyList } from './parsePharmacyList'
import { useCorrectFiltersLabel } from './useCorrectFiltersLabel'

// These keys are found in the raw care detail object we get from the API.
// This array helps us turn those keys into a single `filters` object that
// makes it easier to generate the filter dropdowns in the UI.
const filterKeys = [
  'type',
  'receiveAt',
  'diagnosis',
  'version',
  'brand',
  'form',
  'dosage',
  'quantity',
]

/**
 * Parses the care detail object into a more usable format. See:
 * https://miro.com/app/board/uXjVL3ASPgo=/?moveToWidget=3458764623558442778&cot=14
 *
 * @param {Object} careDetail - The care detail object
 * @returns {Object} The parsed care detail
 */
export const parseCareDetail = R.pipe(
  // first create a list of filters based on care details
  // these filters will be saved with the care detail
  mapObjectKeys({
    filters: R.pipe(
      // pick from the filterKeys above
      R.pick(filterKeys),
      // for each of the filterKeys found in the care item,
      // turn the filterKey and it's value into pairs (i.e. [filterKey, options])
      R.toPairs,
      // turn the pairs into a list of objects
      // that will become our filters.
      R.map(([filterKey, options]) => ({
        name: filterKey,
        label: R.pipe(
          parseFromCamelCase,
          humanizeString,
        )(useCorrectFiltersLabel(filterKey)),
        // because we don't get any values passed in, we want
        // to turn the label that we have, into our value.
        options: R.pipe(
          R.map(
            mapObjectKeys({
              // making the label human readible, because it is simply display, should not affect data
              label: R.pipe(R.prop('label'), label => {
                // for brand specific filters, we want to add (generic) or (brand) to the label
                if (filterKey === 'brand') {
                  const suffix = options.reduce((acc, option) => {
                    // in the off chance scriptsave doesn't return a queryMap, skip this option
                    if (R.isNil(option.queryMap)) {
                      return acc
                    }
                    // LIGHTS-584 added brand or generic txt to drug label options
                    if (label === option.queryMap.drugName) {
                      if (option.queryMap.brandedOrGenericId === 'G') {
                        return ' (generic)'
                      }
                      if (option.queryMap.brandedOrGenericId === 'B') {
                        return ' (brand)'
                      }
                    }
                    return acc
                  }, '')
                  label = label + suffix
                }

                // Only convert to sentence case if we have a constant in the label, otherwise we may
                // lowercase a string that was not intended to display in lowercase (ie. 'OTC COVID-19' should
                // not appear as 'Otc covid-19')
                return _isConstantRegex(label) ? humanizeString(label) : label
              }),
              subLabel: R.prop('subLabel'),
              value: option =>
                `${R.prop('label')(option)}${
                  R.prop('subLabel')(option) || ''
                }`.toLowerCase(), //This is to establish a unique key to select appropriate option in case of label/sublabel option
              title: R.pipe(R.prop('label'), label => {
                // Only convert to sentence case if we have a constant in the label, otherwise we may
                // lowercase a string that was not intended to display in lowercase (ie. 'OTC COVID-19' should
                // not appear as 'Otc covid-19')
                return _isConstantRegex(label) ? humanizeString(label) : label
              }),
            }),
          ),

          // sort the options by label (alphabetically)
          R.sort((a, b) => (a.label < b.label ? -1 : 1)),
        )(options),
      })),
      // Filter out any filters that have empty options arrays, except for quantity which has special handling
      R.filter(
        filter =>
          filter.name === 'quantity' ||
          (filter.options && filter.options.length > 0),
      ),
      R.map((filter = {}) => {
        // When ScriptSave does not return a quantity to us, the backend
        // defaults the drug's quantity to 1 in care details and preview
        // coverage. This is just making sure this is what gets displayed
        if (filter.name === 'quantity' && R.isEmpty(filter.options)) {
          return {
            ...filter,
            options: [
              {
                label: 1,
                selected: true,
                title: 1,
                value: null,
              },
            ],
          }
        }

        // If a filter has options but none are selected, or if there's only one option,
        // ensure at least one option is marked as selected
        if (filter.options && filter.options.length > 0) {
          // Check if we have a single option (always mark it as selected)
          if (filter.options.length === 1) {
            return {
              ...filter,
              options: [{ ...filter.options[0], selected: true }],
            }
          }

          // Check if no options are selected
          const hasSelectedOption = filter.options.some(
            option => option.selected === true,
          )
          if (!hasSelectedOption) {
            // Mark the first option as selected
            return {
              ...filter,
              options: [
                { ...filter.options[0], selected: true },
                ...filter.options.slice(1),
              ],
            }
          }
        }

        return filter
      }),
    ),
  }),

  // parse the rest based on what kind of care we have
  R.evolve({
    // if the care detail has pharmacies, parse those
    // we want to group a flat list of pharmacies, like many
    // walmarts or rite-aids and combine them into one,
    // grouped by their name.
    pharmacies: R.pipe(
      // group the pharmacies
      // then store in as ordered map to use in a dropdown
      parsePharmacyList,
    ),
    // if the care detail has facilities, parse those
    // we're going to just turn the list of facilities
    // into an ordered map so we can use it in a dropdown.
    facilities: arr => arr,
  }),

  // because we only want the filters that we've parsed out
  // we'll get rid of the things we parsed those filters from.
  // we don't need them anymore.
  R.omit(filterKeys),
)

/** -=-=-=-=-=- PRIVATE METHODS -=-=-=-=-=- */

function _isConstantRegex(input) {
  const regex = /[A-Z]+_[A-Z]+/
  return regex.test(input)
}
